import { ManeuverSign } from './maneuverSign';
import type { DirectionRow } from './directionsList';

/**
 * The Material icon a maneuver is drawn with.
 *
 * Kept apart from the component so the mapping can be tested: a sign with no icon falls back
 * to a straight arrow, which reads as "carry on" — the one wrong answer that is never
 * alarming. The phones draw the same shapes from the same signs.
 */
export function maneuverIconName(sign: number): string {
  switch (sign) {
    case ManeuverSign.U_TURN_LEFT:
    case ManeuverSign.U_TURN_RIGHT:
      return 'u_turn_left';
    case ManeuverSign.KEEP_LEFT:
      return 'turn_slight_left';
    case ManeuverSign.KEEP_RIGHT:
      return 'turn_slight_right';
    case ManeuverSign.TURN_SHARP_LEFT:
      return 'turn_sharp_left';
    case ManeuverSign.TURN_LEFT:
      return 'turn_left';
    case ManeuverSign.TURN_SLIGHT_LEFT:
      return 'turn_slight_left';
    case ManeuverSign.TURN_SLIGHT_RIGHT:
      return 'turn_slight_right';
    case ManeuverSign.TURN_RIGHT:
      return 'turn_right';
    case ManeuverSign.TURN_SHARP_RIGHT:
      return 'turn_sharp_right';
    case ManeuverSign.USE_ROUNDABOUT:
      return 'rotate_right';
    case ManeuverSign.FINISH:
    case ManeuverSign.REACHED_VIA:
      return 'sports_score';
    default:
      return 'straight';
  }
}

/** The route's own beginning, which is a place rather than a turn. */
export const START_ICON = 'radio_button_checked';

/**
 * A note somebody wrote about a place, which is not a maneuver at all.
 *
 * `info` rather than a note glyph: it is what the type is called in the picker ("General
 * info"), and it is already used elsewhere in the planner, so it is known to exist in the
 * Material Symbols subset the app loads.
 */
export const NOTE_ICON = 'info';

/** A segment drawn as a straight line, which has no turn to draw — points joined by a line. */
export const STRAIGHT_LINE_ICON = 'linear_scale';

/**
 * The icon a row in the directions list is drawn with.
 *
 * Here rather than in the panel because the order of these three questions is the whole
 * behaviour, and one of them has already been got wrong: a note is matched on its *kind*, not
 * its sign, because "General info" and "Continue straight" both carry sign 0 — testing the
 * sign gave a real straight-ahead direction a note's icon.
 */
export function directionRowIcon(row: DirectionRow): string {
  // The route's own beginning, whatever sign the first stored entry happens to carry.
  if (row.index === 0) return START_ICON;
  // A note has no direction. It carries a `continue` sign purely to travel through a pipeline
  // that positions everything by turn, so drawing an arrow for it would be a lie.
  if (row.cueKind === 'note') return NOTE_ICON;
  if (row.straightLineMeters !== undefined) return STRAIGHT_LINE_ICON;
  return maneuverIconName(row.sign);
}

/** Whether a row's glyph points somewhere, so it can be turned to the line. A flag, a note, a straight line and the start ring only tip over. */
export function directionRowPointsSomewhere(row: DirectionRow): boolean {
  const icon = directionRowIcon(row);
  return (
    icon !== START_ICON &&
    icon !== NOTE_ICON &&
    icon !== STRAIGHT_LINE_ICON &&
    icon !== maneuverIconName(ManeuverSign.FINISH)
  );
}

/**
 * The turns either side of a row, for bounding where its glyph may snap and how far back its
 * approach is measured.
 *
 * Only rows that sit on a corner count. The start ring is the route's beginning, not a turn,
 * and a note marks a place rather than a junction — letting either bound the approach would
 * shorten it for nothing, which is exactly the short-chord wobble the bound is there to avoid.
 * The arrival does count as the next row: nothing past it can be this row's corner.
 */
export function turnNeighbours(
  rows: DirectionRow[],
  row: DirectionRow
): { previousMeters?: number; nextMeters?: number } {
  const corners = rows.filter((r) => r.index !== 0 && r.cueKind !== 'note' && r.straightLineMeters === undefined);
  const before = corners.filter((r) => r.distanceFromStartMeters < row.distanceFromStartMeters);
  const after = corners.filter((r) => r.distanceFromStartMeters > row.distanceFromStartMeters);
  return {
    previousMeters: before.length ? before[before.length - 1].distanceFromStartMeters : undefined,
    nextMeters: after.length ? after[0].distanceFromStartMeters : undefined
  };
}
